import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Button } from '@/components/serge'
import { cn } from '@/lib/utils'
import { FolderOpen, Plus, Loader2, Trash2 } from 'lucide-react'
import { useAuthStore } from '@/stores/authStore'
import toast from 'react-hot-toast'
import DeliverablesGrid from '@/components/serge-workspace/DeliverablesGrid'
import { getSergeProjects, createSergeProject, deleteSergeProject } from '@/services/sergeApi'
import type { SergeProject } from '@/services/sergeApi'

export default function SergeProjectsPage() {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const isAuthenticated = !!user
  
  const [projects, setProjects] = useState<SergeProject[]>([])
  const [currentProject, setCurrentProject] = useState<SergeProject | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [showCreateModal, setShowCreateModal] = useState(false)
  const [newProjectName, setNewProjectName] = useState('')
  const [newProjectDesc, setNewProjectDesc] = useState('')

  // Rediriger si non authentifié
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login')
    }
  }, [isAuthenticated, navigate])

  // Charger les projets au montage
  useEffect(() => {
    if (isAuthenticated) {
      loadProjects()
    }
  }, [isAuthenticated])

  const loadProjects = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const data = await getSergeProjects()
      setProjects(data)
    } catch (err) {
      setError('Erreur lors du chargement des projets')
    } finally {
      setIsLoading(false)
    }
  }

  // Sélectionner le premier projet par défaut
  useEffect(() => {
    if (projects.length > 0 && !currentProject) {
      setCurrentProject(projects[0])
    }
  }, [projects, currentProject, setCurrentProject])

  const handleCreateProject = async () => {
    if (!newProjectName.trim()) return

    setIsLoading(true)
    try {
      const project = await createSergeProject({
        name: newProjectName,
        description: newProjectDesc || undefined,
      })
      setShowCreateModal(false)
      setNewProjectName('')
      setNewProjectDesc('')
      setCurrentProject(project)
      await loadProjects()
      navigate(`/dashboard/serge-projects/${project.id}/upload`)
      toast.success('Projet créé')
    } catch (err) {
      toast.error('Erreur lors de la création')
    } finally {
      setIsLoading(false)
    }
  }

  const handleDeleteProject = async (e: React.MouseEvent, projectId: number, projectName: string) => {
    e.stopPropagation()
    if (!confirm(`Supprimer le projet "${projectName}" ? Cette action est irreversible.`)) {
      return
    }

    try {
      await deleteSergeProject(projectId)
      await loadProjects()
      if (currentProject?.id === projectId) {
        setCurrentProject(null)
      }
      toast.success('Projet supprimé')
    } catch {
      toast.error('Erreur lors de la suppression')
    }
  }

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / (1000 * 60))
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

    if (diffMins < 60) return `Il y a ${diffMins} min`
    if (diffHours < 24) return `Il y a ${diffHours}h`
    if (diffDays < 7) return `Il y a ${diffDays} jour${diffDays > 1 ? 's' : ''}`
    return date.toLocaleDateString('fr-FR')
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'draft':
        return 'Brouillon'
      case 'active':
        return 'Actif'
      case 'archived':
        return 'Archive'
      default:
        return status
    }
  }

  return (
    <div className="min-h-screen -mx-6 -my-8 bg-bg p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        <header className="border border-border bg-surface p-6">
          <div className="flex flex-wrap items-center justify-between gap-6">
            <div>
              <p className="text-xs font-mono text-foreground-muted uppercase tracking-[0.35em]">
                Projets
              </p>
              <h1 className="text-3xl font-bold text-foreground mt-1">Selectionner un projet</h1>
              <p className="text-sm text-foreground-secondary mt-2">
                Accedez a vos documents et livrables en cours.
              </p>
            </div>
            <Button
              variant="primary"
              size="md"
              className="gap-2"
              onClick={() => setShowCreateModal(true)}
            >
              <Plus className="w-4 h-4" />
              Nouveau projet
            </Button>
          </div>
        </header>

        {error && (
          <div className="border border-red-500/50 bg-red-500/10 p-4">
            <p className="text-sm text-red-500">{error}</p>
          </div>
        )}

        {isLoading && projects.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 text-primary animate-spin" />
            <span className="ml-3 text-sm text-foreground-muted">Chargement des projets...</span>
          </div>
        ) : projects.length === 0 ? (
          <div className="border border-border bg-surface p-12 text-center">
            <FolderOpen className="w-12 h-12 text-foreground-muted mx-auto" />
            <h2 className="text-xl font-semibold text-foreground mt-4">Aucun projet</h2>
            <p className="text-sm text-foreground-secondary mt-2">
              Creez votre premier projet pour commencer.
            </p>
            <Button
              variant="primary"
              size="md"
              className="mt-6 gap-2"
              onClick={() => setShowCreateModal(true)}
            >
              <Plus className="w-4 h-4" />
              Creer un projet
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-12 lg:col-span-5 space-y-4">
              {projects.map((project) => (
                <button
                  key={project.id}
                  type="button"
                  onClick={() => setCurrentProject(project)}
                  className={cn(
                    'w-full text-left border bg-surface p-6 transition-colors',
                    currentProject?.id === project.id
                      ? 'border-primary/50'
                      : 'border-border hover:border-border-hover'
                  )}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-mono text-foreground-muted uppercase tracking-widest">
                      {getStatusLabel(project.status)}
                    </span>
                    <FolderOpen
                      className={cn(
                        'w-4 h-4',
                        currentProject?.id === project.id ? 'text-primary' : 'text-foreground-muted'
                      )}
                    />
                  </div>
                  <h2 className="text-xl font-semibold text-foreground mt-4">{project.name}</h2>
                  <p className="text-sm text-foreground-secondary mt-2">
                    {project.description || 'Workspace projet'}
                  </p>
                  <div className="flex items-center justify-between mt-6">
                    <p className="text-xs font-mono text-foreground-muted">
                      {formatDate(project.updatedAt || project.createdAt)}
                    </p>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={(e) => handleDeleteProject(e, project.id, project.name)}
                        className="text-foreground-muted hover:text-red-500 transition-colors"
                        title="Supprimer le projet"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                      <Link
                        to={`/dashboard/serge-projects/${project.id}/upload`}
                        className="text-xs font-mono text-primary hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Ouvrir
                      </Link>
                    </div>
                  </div>
                </button>
              ))}
            </div>
            <div className="col-span-12 lg:col-span-7 space-y-4">
              {currentProject && (
                <>
                  <div className="border border-border bg-surface p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-mono text-foreground-muted uppercase tracking-widest">
                          Livrables du projet
                        </p>
                        <h2 className="text-lg font-semibold text-foreground mt-2">
                          {currentProject.name}
                        </h2>
                      </div>
                      <div className="flex items-center gap-3">
                        <Link
                          to={`/dashboard/serge-projects/${currentProject.id}/upload`}
                          className="text-xs font-mono text-foreground-muted hover:text-primary transition-colors"
                        >
                          Workspace
                        </Link>
                        <button
                          type="button"
                          onClick={() => navigate(`/dashboard/serge-projects/${currentProject.id}/export`)}
                          className="text-xs font-mono text-primary hover:underline"
                        >
                          Export
                        </button>
                      </div>
                    </div>
                    <p className="text-sm text-foreground-secondary mt-3">
                      Consultez l etat des livrables. Pour generer ou regenerer, utilisez le workspace &gt; Export afin de
                      respecter l ordre recommande.
                    </p>
                    <div className="mt-4">
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full"
                        onClick={() => navigate(`/dashboard/serge-projects/${currentProject.id}/export`)}
                      >
                        Ouvrir la page Export
                      </Button>
                    </div>
                  </div>
                  <DeliverablesGrid projectId={currentProject.id} allowGenerate={false} />
                </>
              )}
            </div>
          </div>
        )}

        {/* Modal création projet */}
        {showCreateModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-bg/80">
            <div className="border border-border bg-surface p-6 w-full max-w-md">
              <h2 className="text-lg font-semibold text-foreground">Nouveau projet</h2>
              <p className="text-sm text-foreground-secondary mt-2">
                Creez un nouveau workspace pour vos documents.
              </p>

              <div className="mt-6 space-y-4">
                <div className="space-y-2">
                  <label className="text-xs font-mono text-foreground-muted uppercase tracking-widest">
                    Nom du projet
                  </label>
                  <input
                    type="text"
                    value={newProjectName}
                    onChange={(e) => setNewProjectName(e.target.value)}
                    placeholder="Mon projet"
                    className="w-full h-12 px-4 bg-bg border border-border text-foreground font-mono placeholder:text-foreground-muted focus:border-primary focus:outline-none"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-mono text-foreground-muted uppercase tracking-widest">
                    Description (optionnel)
                  </label>
                  <textarea
                    value={newProjectDesc}
                    onChange={(e) => setNewProjectDesc(e.target.value)}
                    placeholder="Description du projet..."
                    rows={3}
                    className="w-full px-4 py-3 bg-bg border border-border text-foreground font-mono placeholder:text-foreground-muted focus:border-primary focus:outline-none resize-none"
                  />
                </div>
              </div>

              <div className="mt-6 flex gap-3">
                <Button
                  variant="outline"
                  size="md"
                  className="flex-1"
                  onClick={() => setShowCreateModal(false)}
                >
                  Annuler
                </Button>
                <Button
                  variant="primary"
                  size="md"
                  className="flex-1"
                  onClick={handleCreateProject}
                  disabled={!newProjectName.trim() || isLoading}
                >
                  {isLoading ? 'Creation...' : 'Creer'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
